import { Dialog, Transition } from '@headlessui/react';
import Close from 'assets/icons/Close';
import Right from 'assets/icons/Right';
import Button from 'common/components/Button';
import Chip from 'common/components/Chip';
import Dropdown from 'common/components/Dropdown';
import LabelledInput from 'common/components/LabelledInput';
import useTranslation from 'common/hooks/useTranslation';
import { cn } from 'common/utils';
import SimpleSwitch from 'offers/components/SimpleSwitch';
import {
  ALL_QUESTION_DATA_TYPES,
  canSubmitQuestion as canSubmitQuestionUtil,
  isChoiceBasedType,
  isMultipleChoice as isMultipleChoiceType,
} from 'offers/utils/questionUtils';
import React, { Fragment, useState } from 'react';

const MultipleOptionCreator = ({
  choices,
  setChoices,
  isMultipleChoice,
  isSingleAnswer,
  setIsSingleAnswer,
  t,
}) => {
  const [newOption, setNewOption] = useState('');

  const addOption = () => {
    if (newOption) {
      setChoices([...choices, newOption]);
      setNewOption('');
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='relative'>
        <div className='w-full'>
          <LabelledInput
            type='text'
            label={t('modals.newQuestion.fields.choices.title')}
            placeholder={t('modals.newQuestion.fields.choices.placeholder')}
            name='multiple-options'
            value={newOption}
            className='pr-14'
            onChange={(e) => setNewOption(e.target.value)}
          />
        </div>
        <div className='absolute right-0.5 bottom-[9px] px-2 border-l border-l-cool-grey/50 bg-white'>
          <Button
            variant='tertiary'
            size='small'
            onClick={addOption}
            disabled={!newOption}
          >
            +
          </Button>
        </div>
      </div>
      {isMultipleChoice && (
        <div className='flex gap-2 justify-start text-xs text-weldon-blue/90'>
          <SimpleSwitch
            isActive={!isSingleAnswer}
            onToggle={() => {
              if (choices?.length > 1) {
                setIsSingleAnswer((prev) => !prev);
              }
            }}
            isDisabled={!(choices?.length > 1)}
          />
          {t('modals.newQuestion.fields.choices.allow-multiple-answers')}
        </div>
      )}
      {choices?.length > 0 && (
        <div className='flex gap-2 flex-wrap mt-1'>
          {choices.map((option, index) => (
            <Chip key={index} variant='grey' className='max-w-full'>
              <span className='truncate'>{option}</span>
              <button
                type='button'
                onMouseDown={(e) => e.preventDefault()}
                className='rounded-full hover:bg-dark-blue/20 focus:outline-none focus:ring-2 focus:ring-dark-blue/20'
                onClick={() =>
                  setChoices(choices.filter((_, i) => i !== index))
                }
              >
                <Close />
              </button>
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
};

const QuestionsModal = ({ isOpen, setIsOpen, questions, setQuestions }) => {
  const { t } = useTranslation('offers\\new');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState('');
  const [choices, setChoices] = useState([]);
  const [isSingleAnswer, setIsSingleAnswer] = useState(true);
  // const [answer, setAnswer] = useState('');

  const choiceBased = isChoiceBasedType(dataType);
  const isMultipleChoice = isMultipleChoiceType(dataType);
  const canSubmitQuestion = canSubmitQuestionUtil({
    name,
    dataType,
    choices,
  });

  const dataTypeOptions = ALL_QUESTION_DATA_TYPES.map((type) => ({
    label: t(`modals.newQuestion.fields.dataType.options.${type}`),
    value: type,
  }));
  const dataTypeLabels = dataTypeOptions.map((o) => o.label);
  const labelToValueMap = Object.fromEntries(
    dataTypeOptions.map(({ label, value }) => [label, value]),
  );

  const closeModal = () => {
    setIsOpen(false);
    setName('');
    setDescription('');
    setDataType('');
    setChoices([]);
    setIsSingleAnswer(true);
    // setAnswer('');
  };

  const AddNewQuestion = async () => {
    setQuestions([
      ...questions,
      {
        name: name,
        description: description,
        data_type: dataType,
        ...(choiceBased && { choices: choices }),
        ...(isMultipleChoice && { single_answer: isSingleAnswer }),
        // answer: answer,
        source: 'manual',
      },
    ]);
    closeModal();
  };

  return (
    <Fragment>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as='div' className='relative z-50' onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </Transition.Child>
          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel
                  className={cn(
                    'flex flex-col w-full overflow-visible max-w-md transform',
                    'rounded bg-white p-10 text-left gap-6',
                    'align-middle shadow-xl transition-all relative',
                  )}
                >
                  <Dialog.Title
                    as='div'
                    className='flex flex-col w-full items-center gap-8'
                  >
                    <h1 className=' text-2xl font-semibold text-dark-charcoal'>
                      {t('modals.newQuestion.title')}
                    </h1>
                  </Dialog.Title>
                  <Dialog.Description
                    as='div'
                    className='flex flex-col w-full items-center gap-8'
                  >
                    <h1 className='text-dark-charcoal text-center'>
                      {t('modals.newQuestion.description')}
                    </h1>
                  </Dialog.Description>
                  <LabelledInput
                    type='text'
                    label={t('modals.newQuestion.fields.name.title')}
                    name='name'
                    placeholder={t(
                      'modals.newQuestion.fields.name.placeholder',
                    )}
                    onChange={(event) => setName(event.target.value)}
                  />
                  {choiceBased && (
                    <MultipleOptionCreator
                      choices={choices}
                      setChoices={setChoices}
                      isMultipleChoice={isMultipleChoice}
                      isSingleAnswer={isSingleAnswer}
                      setIsSingleAnswer={setIsSingleAnswer}
                      t={t}
                    />
                  )}
                  <LabelledInput
                    type='textarea'
                    label={t('modals.newQuestion.fields.description.title')}
                    name='description'
                    placeholder={t(
                      'modals.newQuestion.fields.description.placeholder',
                    )}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                  <Dropdown
                    options={dataTypeLabels}
                    label={t('modals.newQuestion.fields.dataType.title')}
                    placeholder={t(
                      'modals.newQuestion.fields.dataType.placeholder',
                    )}
                    onChange={(label) => setDataType(labelToValueMap[label])}
                  />
                  <div className='w-full flex items-center justify-center gap-2'>
                    <Button variant='secondary' onClick={closeModal}>
                      {t('modals.newQuestion.buttons.cancel')}
                    </Button>
                    <Button
                      variant='primary'
                      onClick={AddNewQuestion}
                      disabled={!canSubmitQuestion}
                    >
                      {t('modals.newQuestion.buttons.add')}
                      <Right size='16px' color='#FFFFFF' />
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  );
};

export default QuestionsModal;
